import { apiService } from "src/services";
import { generateFileName } from "src/pages/Studies/transformer";
import { fileTypes, IDownloadUrlData, IFileDownloadPayload, IPaginatedResult, IStandardApiResponse } from "src/pages/Studies/models/interfaces";
import {
	AskKuralLogsExportPayload,
	AuditLogExportPayload,
	IExportUserObservabilityParams,
	IPermanentDeletePayload,
	IPermanentDeleteResponse,
	IProfile,
	IRole,
	IServiceAccount,
	IServiceAccountKey,
	IAdminTranscriptExportPayload,
	IAdminTranscriptFilters,
	IAdminTranscriptListResponse,
	IAdminTranscriptFacets,
	IAdminTranscriptStagingRefreshResponse,
	IUserDetails,
	ToggleNotificationActionPayload,
	UpdateNotificationActionPayload,
	ProvisionScope,
} from "../models/interfaces";
import { parseFilename } from "../transformer";
import { AxiosResponse } from "axios";
import { GroupType } from "../components/Monitoring/interfaces";

export const saveUserDetails = async (userDetails: IUserDetails) => {
	return await apiService.post("/admin/user", userDetails);
};

export const getProfiles = async () => {
	return await apiService.get("/admin/profiles");
};

export const saveProfileDetails = async (profileDetails: IProfile) => {
	return await apiService.post("/admin/profiles", profileDetails);
};

export const updateProfileDetails = async (profileDetails: IProfile) => {
	return await apiService.put("/admin/profiles", profileDetails);
};

export const updateUserDetails = async (userDetails: IUserDetails) => {
	return await apiService.put("/admin/user", userDetails);
};

export const DeleteUsers = async (details) => {
	return await apiService.post(`/admin/users/delete`, details);
};

export const enableAccess = async (userDetails: IUserDetails[]) => {
	return await apiService.put(`/admin/users/access`, userDetails);
};

export const getUsers = async (
	page: number,
	page_size: number,
	search: string,
	sort_ascending?: boolean,
	profile: string[] = [],
	role: string[] = [],
	enable_access?: boolean | null,
	expiry_ascending?: boolean,
): Promise<IStandardApiResponse<IPaginatedResult<IUserDetails>>> => {
	const params: Record<string, string | number | boolean> = { page, page_size };
	if (search) params.search = search;
	if (profile.length > 0) params.profiles = JSON.stringify(profile);
	if (role.length > 0) params.roles = JSON.stringify(role);
	if (sort_ascending !== undefined) params.sort_ascending = sort_ascending;
	if (expiry_ascending !== undefined) params.expiry_ascending = expiry_ascending;
	if (enable_access !== undefined && enable_access !== null) params.enable_access = enable_access;

	return await apiService.get<IStandardApiResponse<IPaginatedResult<IUserDetails>>>("/admin/users/paginated", params);
};

export const adminSync = async () => {
	return await apiService.get("/admin/sync");
};

export const deleteProfile = async (values) => {
	return await apiService.delete("/admin/profiles", { ...values });
};

export const downloadFile = async () => {
	const fileName = generateFileName(fileTypes.addUsers, "");
	try {
		const response = await apiService.get<IStandardApiResponse<IDownloadUrlData>>("admin/download");
		const downloadUrl = response.data?.url;
		if (!downloadUrl) {
			throw new Error("No download URL received");
		}
		const fileResponse = await fetch(downloadUrl);
		if (fileResponse.ok) {
			const blob = await fileResponse.blob(); // Convert the response to a blob
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = fileName; // Use the original file name for download
			document.body.appendChild(a); // Append the anchor to the body
			a.click(); // Trigger the download
			document.body.removeChild(a); // Remove the anchor from the body
			window.URL.revokeObjectURL(url); // Clean up the object URL
		} else {
			throw new Error("Failed to download file");
		}
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

export const uploadUsers = async (file: Blob) => {
	const formData = new FormData();
	formData.append("file", file);
	return await apiService.post("/admin/upload_users", formData);
};

export const getStudies = async (page: number, page_size: number, search: string, profile, sort_ascending: boolean, study_type) => {
	const profiles = JSON.stringify(profile);
	const study_types = JSON.stringify(study_type);
	return await apiService.get("/admin/studies", { page, page_size, search, profiles, sort_ascending, study_types });
};

export const deleteStudy = async (payload) => {
	return await apiService.put("/admin/studies", payload);
};

export const getFeatures = async () => {
	return await apiService.get("/admin/features");
};

export const createFeature = async (payload) => {
	return await apiService.post("/admin/feature", payload);
};

export const updateFeature = async (payload) => {
	return await apiService.put("/admin/feature", payload);
};

export const deleteFeature = async (payload) => {
	return await apiService.post("/admin/feature/delete", payload);
};

export const updateFeatureStatus = async (payload) => {
	return await apiService.put("/admin/feature/status", payload);
};

export const updateRole = async (payload: { role_id: string; provision_scope?: ProvisionScope }) => {
	return await apiService.put("/admin/role", payload);
};

export const saveRoleDetails = async (roleDetails: IRole) => {
	return await apiService.post("/admin/role", roleDetails);
};

export const deleteRole = async (role_name: string, role_id: string) => {
	return await apiService.delete("/admin/role", { role_name, role_id });
};

export const updateStudyOwner = async (ownerDetails: any) => {
	return await apiService.post("/admin/collaborator/owner", ownerDetails);
};

export const getEventLogs = async (page: number, page_size: number) => {
	return await apiService.get("/admin/logs", { page, page_size });
};

export const getAdminTranscriptFacets = async () => {
	return await apiService.get<{ data: IAdminTranscriptFacets }>("/admin/transcripts/facets");
};

export const getAdminTranscripts = async (params: IAdminTranscriptFilters & { page: number; page_size: number }) => {
	return await apiService.get<{ data: IAdminTranscriptListResponse }>("/admin/transcripts", params);
};

export const refreshAdminTranscriptStaging = async () => {
	return await apiService.post<{ data: IAdminTranscriptStagingRefreshResponse }>("/admin/transcripts/staging/refresh", {});
};

export const exportAdminTranscripts = async (payload: IAdminTranscriptExportPayload, fileName = "admin-transcripts.zip") => {
	const response = await apiService.post<Blob, IAdminTranscriptExportPayload>("/admin/transcripts/export", payload, { responseType: "blob" });
	if (!response) {
		throw new Error("No export data received");
	}
	const url = window.URL.createObjectURL(response);
	const anchor = document.createElement("a");
	try {
		anchor.href = url;
		anchor.download = fileName;
		document.body.appendChild(anchor);
		anchor.click();
	} finally {
		anchor.remove();
		window.setTimeout(() => window.URL.revokeObjectURL(url), 100);
	}
};

export const getUserActivityLogs = async (page: number, page_size: number) => {
	return await apiService.get("/activity/paginated", { page, page_size });
};

export const getFeaturesRequestList = async (payload: Record<string, unknown>): Promise<IStandardApiResponse<IPaginatedResult<unknown>>> => {
	return await apiService.get<IStandardApiResponse<IPaginatedResult<unknown>>>("admin/feature-interested-users", payload);
};

export const featureAccessApproval = async (payload: Record<string, unknown>): Promise<IStandardApiResponse<unknown>> => {
	return await apiService.post<IStandardApiResponse<unknown>>("admin/feature-access/approve", payload);
};

export const downloadLogs = async () => {
	const fileName = generateFileName(fileTypes.logs, "");
	try {
		const response = await apiService.downloadFile("admin/log_download");
		if (!response) {
			throw new Error("No data received");
		}
		const url = window.URL.createObjectURL(response);
		const a = document.createElement("a");
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	} catch (err) {
		console.error("Error downloading file: ", err);
		throw err;
	}
};

// ============ API PAYLOAD INTERFACE ============
export interface DashboardPayload {
	time_period: string;
	profile: string;
	user_email?: string;
}

export const getDashboardKPIs = async (payload: DashboardPayload) => {
	return await apiService.get("activity/dashboard-kpis", payload);
};

export const getDashboardV2 = async (payload: DashboardPayload) => {
	return await apiService.get("activity/dashboard-v2", payload);
};

export const getPeakUsage = async (payload: DashboardPayload) => {
	return await apiService.get("activity/peak-usage", payload);
};

export const getAskKuralLatency = async (payload: DashboardPayload) => {
	return await apiService.get("activity/ask-kural-latency-distribution", payload);
};

export const getRecentActiveUsers = async (limit: number, group?: GroupType) => {
	const safeLimit = Math.max(1, Math.min(Math.floor(limit), 200));
	const params: { limit: number; group?: GroupType } = { limit: safeLimit };
	if (group && group !== GroupType.All) {
		params.group = group;
	}
	return await apiService.get("activity/users/recent", params);
};

export const getMetricsMetadata = async () => {
	return await apiService.get("activity/metrics/metadata");
};

export const triggerMetricsRefresh = async () => {
	return await apiService.post("activity/metrics/refresh", {});
};

export const downloadUserObservabilityExport = async (params: { time_period: string; granularity: string; user_emails?: string[] }): Promise<void> => {
	const queryParams = new URLSearchParams();
	queryParams.append("time_period", params.time_period);
	queryParams.append("granularity", params.granularity);

	if (params.user_emails && params.user_emails.length > 0) {
		queryParams.append("user_emails", JSON.stringify(params.user_emails));
	}

	const response = await apiService.get(`/activity/export/excel?${queryParams.toString()}`);

	// Cast to any to access .data
	const responseData = response as any;
	const presignedUrl = responseData?.data?.url;
	const fileName = responseData?.data?.file_name;

	if (!presignedUrl) {
		throw new Error("No download URL received");
	}

	const fileResponse = await fetch(presignedUrl);
	if (!fileResponse.ok) {
		throw new Error("Failed to download file");
	}

	const blob = await fileResponse.blob();
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName || `user_observability_export_${new Date().toISOString().split("T")[0]}.xlsx`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};
export const createServiceAccount = async (details: any) => {
	return await apiService.post("/admin/service-account", details);
};
export const getServiceAccounts = async (
	page: number,
	page_size: number,
	search: string,
	profiles: string[],
	roles: string[],
	enable_access?: boolean,
): Promise<IStandardApiResponse<IPaginatedResult<IServiceAccount>>> => {
	const profileFilters = JSON.stringify(profiles);
	const roleFilters = JSON.stringify(roles);

	return await apiService.get<IStandardApiResponse<IPaginatedResult<IServiceAccount>>>("/admin/service-accounts", {
		page,
		page_size,
		search,
		profiles: profileFilters,
		roles: roleFilters,
		enable_access,
	});
};
export const updateServiceAccount = async (details: any) => {
	return await apiService.put("/admin/service-account", details);
};
export const deleteServiceAccounts = async (ids: string[]) => {
	return await apiService.delete("/admin/service-accounts", { ids });
};
export const updateServiceAccountAccess = async (id: string, enable_access: boolean) => {
	return await apiService.put("/admin/service-account/access", {
		id,
		enable_access,
	});
};
export const generateServiceAccountKey = async (payload: IServiceAccountKey) => {
	return await apiService.post("/admin/service-account/generate-key", payload);
};
export const getServiceAccountKeys = async (service_account_id: string) => {
	return await apiService.get("/admin/service-account/keys", {
		service_account_id,
	});
};
export const revokeServiceAccountKey = async (service_account_id: string, key_id: string) => {
	return await apiService.delete("/admin/service-account/revoke-key", {
		service_account_id,
		key_id,
	});
};
export const getNotificationActions = async () => {
	return await apiService.get("/admin/notification-actions");
};

export const toggleNotificationAction = async (payload: ToggleNotificationActionPayload) => {
	return await apiService.put("/admin/notification-actions/toggle", payload);
};

export const updateNotificationAction = async (payload: UpdateNotificationActionPayload) => {
	return await apiService.put("/admin/notification-actions", payload);
};

const ALLOWED_BLOB_TYPES = new Set(["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]);

const ALLOWED_EXTENSIONS = new Set([".xlsx"]);
const DEFAULT_FILENAME = "audit_logs_export.xlsx";

// ============ FILENAME SANITIZER ============
/**
 * Securely sanitizes a filename derived from external input (e.g. Content-Disposition header).
 *
 * 1. Strips to basename — removes everything before the last path separator,
 *    preventing path traversal regardless of encoding tricks.
 * 2. Replaces any non-safe characters with underscores.
 * 3. Validates the extension against an explicit allowlist.
 * 4. Falls back to DEFAULT_FILENAME if anything looks wrong.
 */
function sanitizeFilename(raw: string | null | undefined): string {
	if (!raw) return DEFAULT_FILENAME;

	// Step 1: Extract basename — strip any directory components (/, \, encoded variants)
	// This is the key defense against path traversal
	const decoded = decodeURIComponent(raw);
	const basename = decoded.split(/[/\\]/).pop()?.trim() || "";

	if (!basename) return DEFAULT_FILENAME;

	// Step 2: Replace unsafe characters with underscores
	const SAFE_CHAR_RE = /[^a-zA-Z0-9._\-() ]/g;
	const sanitized = basename.replace(SAFE_CHAR_RE, "_").trim();

	if (!sanitized) return DEFAULT_FILENAME;

	// Step 3: Validate extension against allowlist
	const extMatch = sanitized.match(/\.[^.]+$/);
	const ext = extMatch ? extMatch[0].toLowerCase() : "";

	if (!ALLOWED_EXTENSIONS.has(ext)) return DEFAULT_FILENAME;

	// Step 4: Reject if it still looks suspicious (e.g. ".." sequences survived)
	if (sanitized.includes("..")) return DEFAULT_FILENAME;

	return sanitized;
}

// ============ DOWNLOAD FUNCTION ============
export const downloadAuditLogsExport = async (payload: AuditLogExportPayload): Promise<void> => {
	try {
		const blob: Blob = await apiService.post("/admin/audit-export", payload, {
			responseType: "blob",
		});

		// Strict allowlist check — rejects any unexpected MIME type
		if (!ALLOWED_BLOB_TYPES.has(blob.type)) {
			throw new Error(`Blocked download: unexpected blob type "${blob.type}"`);
		}

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = sanitizeFilename(DEFAULT_FILENAME); // swap DEFAULT_FILENAME with parseFilename(header) if headers become available
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	} catch (err) {
		console.error("Error downloading audit logs export:", err);
		throw err;
	}
};

const ASK_KURAL_LOGS_DEFAULT_FILENAME = "ask_kural_logs_export.xlsx";

interface AskKuralLogsExportResponse {
	_id?: string;
	message?: string;
	error?: string | null;
	data?: {
		url?: string;
		filename?: string;
		cached?: boolean;
		expires_in?: number;
		row_count?: number;
	};
}

export const downloadAskKuralLogsExport = async (payload: AskKuralLogsExportPayload): Promise<void> => {
	try {
		const response: AskKuralLogsExportResponse = await apiService.post("/admin/ask-kural-export", payload);
		const url = response?.data?.url;
		if (!url) throw new Error("No download URL returned");

		// Direct anchor navigation avoids fetch-based CORS preflights on the S3 presigned URL.
		// The bucket sets Content-Disposition: attachment via the response-content-disposition
		// query param, so the browser saves the file with the right name.
		const a = document.createElement("a");
		a.href = url;
		a.download = response.data?.filename || ASK_KURAL_LOGS_DEFAULT_FILENAME;
		a.rel = "noopener";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	} catch (err) {
		console.error("Error downloading ask kural logs export:", err);
		throw err;
	}
};

export const permanentDeleteStudy = async (payload: IPermanentDeletePayload): Promise<IPermanentDeleteResponse> => {
	const res: any = await apiService.post("/admin/studies/permanent-delete", payload as any);
	return res?.data ?? res;
};
